import { makeObservable, observable, action, computed, reaction } from 'mobx';
import { getRoot } from 'mobx-easy';
import { ExtendedPolylineData, MarkerData } from '@bringg-frontend/bringg-map';
import { Task } from '@bringg/types';

import RootStore from 'bringg-web/stores/root-store';
import { routesMonitoringRootStore } from './routes-monitoring-root-store';
import { etaCalculatorProvider } from '../../shared/utils/eta-calculator/eta-calculator-provider';
import {
	generateDriverMarker,
	generatePolyline,
	generateTaskMarker,
	generateTeamMarker
} from '../../shared/utils/map/map-utils';
import { getFirstTask, getRouteTasks } from '../utils/route-utils';

class RoutesMonitoringMapViewStore {
	polylines: ExtendedPolylineData[];

	_tasksMarkers: MarkerData[];

	_driversMarkers: MarkerData[] = [];

	_visibleDrivers: Set<number> = new Set();

	private _teamsMarkers: MarkerData[];

	private disposeSignal: AbortSignal;

	constructor(disposeSignal: AbortSignal) {
		makeObservable(this, {
			polylines: observable,
			_tasksMarkers: observable,
			_driversMarkers: observable,
			setMarkers: action,
			setPolylines: action,
			setTeamMarkers: action,
			setDriversMarkers: action,
			markers: computed,
			driverMarkers: computed
		});

		reaction(
			() => routesMonitoringRootStore.getStore().routesMonitoringTableViewStore.viewedRoutes,
			async (curr, prev) => {
				if (Object.keys(curr).length !== Object.keys(prev).length) {
					this.setMarkers();
					await this.setPolylines();
				}
			},
			{ signal: disposeSignal }
		);

		this.disposeSignal = disposeSignal;
	}

	listenToItemsChange = () => {
		reaction(
			() => routesMonitoringRootStore.getStore().routesMonitoringStore.currentTableDefinition.groups,
			() => {
				if (routesMonitoringRootStore.getStore().routesMonitoringTableViewStore.checkedRoutes.length) {
					this.refreshDirections();
					this.setDriversMarkers();
				}
			},
			{ signal: this.disposeSignal }
		);
	};

	async setPolylines() {
		const { routesMonitoringTableViewStore } = routesMonitoringRootStore.getStore();
		const res = await etaCalculatorProvider.getEtaCalculations(routesMonitoringTableViewStore.viewedRoutes);
		const lines = [];
		res.forEach(response => {
			if (response.status === 'fulfilled') {
				const color = routesMonitoringTableViewStore.runToColor[response.value[0].updatedRoutesInfo[0].run_id];
				const line = generatePolyline(response.value, color);
				lines.push(...line);
			}
		});

		this.polylines = lines;
	}

	setMarkers = () => {
		const { routesMonitoringTableViewStore } = routesMonitoringRootStore.getStore();
		const tasksObjects: Task[] = routesMonitoringTableViewStore.viewedRoutes.reduce((tasks, routeId) => {
			const t = getRouteTasks(Number(routeId));
			if (t) {
				tasks.push(...t);
			}
			return tasks;
		}, []);

		this._tasksMarkers = tasksObjects.map((task, index, tasks) => {
			const color = routesMonitoringRootStore.getStore().routesMonitoringTableViewStore.runToColor[task.run_id];
			return generateTaskMarker(task, index, tasks, color);
		});
		this.setTeamMarkers();
		this.setDriversMarkers();
	};

	refreshDirections = () => {
		const { routesMonitoringStore, routesMonitoringTableViewStore } = routesMonitoringRootStore.getStore();
		const routesToRecalculate = routesMonitoringStore.routes.filter(route => {
			return (
				routesMonitoringTableViewStore.isRouteViewed(route.id) &&
				etaCalculatorProvider.shouldRecalculateDirection(route)
			);
		});

		if (routesToRecalculate.length) {
			routesToRecalculate.forEach(route => {
				etaCalculatorProvider.resetRouteCalculation(route.id);
			});
			this.setMarkers();
			this.setPolylines();
		}
	};

	setDriversMarkers = () => {
		const { driversStore } = getRoot<RootStore>().data;
		const { routesMonitoringTableViewStore } = routesMonitoringRootStore.getStore();

		this._driversMarkers = routesMonitoringTableViewStore.viewedRoutes
			.map(routeId => {
				const task = getFirstTask(routeId);

				if (!task) {
					return undefined;
				}

				const driver = driversStore.get(task.user_id);

				if (!driver) {
					return undefined;
				}
				return generateDriverMarker(
					driver,
					routesMonitoringRootStore.getStore().routesMonitoringTableViewStore.runToColor[routeId]
				);
			})
			.filter(Boolean);
	};

	setTeamMarkers = () => {
		const { teamsStore } = getRoot<RootStore>().data;
		const { routesMonitoringStore, routesMonitoringTableViewStore } = routesMonitoringRootStore.getStore();

		let storeRoutes = routesMonitoringStore.routes;

		if (routesMonitoringTableViewStore.viewedRoutes.length) {
			storeRoutes = [];
			routesMonitoringStore.routes.forEach(route => {
				if (routesMonitoringTableViewStore.isRouteViewed(route.id)) {
					storeRoutes.push(route);
				}
			});
		}

		this._teamsMarkers = storeRoutes
			.map(({ team_id }) => {
				const team = teamsStore.get(team_id);
				if (!team) {
					return undefined;
				}
				return generateTeamMarker(team);
			})
			.filter(Boolean);
	};

	toggleDriver = (routeId: number, isOnMap: boolean) => {
		const route = getRoot<RootStore>().data.runStore.get(routeId);
		if (isOnMap) {
			this._visibleDrivers.add(route.user_id);
		} else {
			this._visibleDrivers.delete(route.user_id);
		}
	};

	get markers() {
		if (this._teamsMarkers) {
			return (this._tasksMarkers || []).concat(...this._teamsMarkers);
		}

		return this._tasksMarkers;
	}

	get driverMarkers() {
		return this._driversMarkers;
	}
}

export default RoutesMonitoringMapViewStore;
